import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { SapAsyncPetitionDataQuery, SapAsyncPetitionDataQueryVariables } from './api.graphql';
import { SAPAsyncPetitionData } from './api.graphql';

type Petition = NonNullable<SapAsyncPetitionDataQuery['petition']>;
type FreePromotion = NonNullable<Petition['freePromotions']>[number];
type QualityScore = Omit<Extract<NonNullable<Petition['qualityScore']>, { __typename: 'QualityScore' }>, '__typename'>;
type WithFreePromotion = Readonly<{ freePromotion: FreePromotion }>;
type WithQualityScore = Readonly<{ qualityScore: QualityScore }>;
type WithBoth = Readonly<{ freePromotion: FreePromotion; qualityScore: QualityScore }>;

// Union of all possible return types
type SAPAsyncPetitionDataResponse = WithFreePromotion | WithQualityScore | WithBoth;

type GetAsyncPetitionDataParams = {
	petitionId: string;
	fetchStarterFreePromotion: boolean;
	fetchQualityScore: boolean;
};
// eslint-disable-next-line complexity
export async function getAsyncPetitionData(
	{ petitionId, fetchStarterFreePromotion, fetchQualityScore }: GetAsyncPetitionDataParams,
	{ gql: { fetch } }: UtilityContext,
): Promise<SAPAsyncPetitionDataResponse | undefined> {
	try {
		const { data } = await fetch<SapAsyncPetitionDataQuery, SapAsyncPetitionDataQueryVariables>({
			query: SAPAsyncPetitionData,
			variables: { slugOrId: petitionId, fetchStarterFreePromotion, fetchQualityScore },
			rejectOnError: false,
		});

		const freePromotion = data?.petition?.freePromotions?.find(
			(promotion) => promotion.sourceLocation === 'auto_free_promotion',
		);
		const qualityScore =
			data?.petition?.qualityScore?.__typename === 'QualityScore' ? data.petition.qualityScore : undefined;

		// if both are requested, only return if both are available
		if (fetchStarterFreePromotion && fetchQualityScore) {
			return freePromotion && qualityScore
				? { freePromotion, qualityScore: { totalScore: qualityScore.totalScore, categories: qualityScore.categories } }
				: undefined;
		}

		// if only one is requested, return if resolved with the value
		if (fetchStarterFreePromotion && freePromotion) return { freePromotion };
		if (fetchQualityScore && qualityScore)
			return { qualityScore: { totalScore: qualityScore.totalScore, categories: qualityScore.categories } };

		return undefined;
	} catch {
		// do nothing, we don't care if this fails
		return undefined;
	}
}
